import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, Eye, Clock, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface PostCardProps {
  id: number;
  title: string;
  slug: string;
  metaDescription: string;
  category: string;
  createdAt: string;
  views: number;
  coverImageUrl?: string;
}

export function PostCard({ title, slug, metaDescription, category, createdAt, views, coverImageUrl }: PostCardProps) {
  const calculateReadingTime = (text: string) => {
    const wordsPerMinute = 200;
    const words = text.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const readingTime = calculateReadingTime(metaDescription);
  const isNew = new Date().getTime() - new Date(createdAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  
  const defaultGradient = "linear-gradient(135deg, hsl(240, 14%, 35%) 0%, hsl(214, 29%, 89%) 100%)";

  return (
    <Link 
      to={`/post/${slug}`} 
      className="block group animate-fade-in-up"
    >
      <Card className="h-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-kopf-secondary overflow-hidden focus-within:ring-2 focus-within:ring-kopf-accent focus-within:ring-offset-2">
        <div className="relative aspect-video overflow-hidden" role="img" aria-label={`Imagem do post: ${title}`}>
          <div 
            className="w-full h-full group-hover:scale-110 transition-transform duration-500"
            style={{
              background: defaultGradient
            }}
          >
            {coverImageUrl && (
              <img 
                src={coverImageUrl} 
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <Badge className="absolute top-3 left-3 bg-kopf-accent text-kopf-text hover:bg-kopf-accent/80 transition-transform hover:scale-105" aria-label={`Categoria: ${category}`}>
            {category}
          </Badge>
          {isNew && (
            <Badge className="absolute top-3 right-3 bg-kopf-primary text-white flex items-center gap-1 animate-pulse" aria-label="Post novo">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              NOVO
            </Badge>
          )}
        </div>
        
        <CardHeader>
          <h2 className="text-xl font-semibold text-kopf-primary group-hover:text-kopf-accent transition-colors line-clamp-2 leading-tight">
            {title}
          </h2>
        </CardHeader>
        
        <CardContent>
          <p className="text-kopf-text/80 mb-4 line-clamp-3 leading-relaxed">
            {metaDescription}
          </p>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-kopf-text/60 flex-wrap">
            <div className="flex items-center gap-1" aria-label={`Publicado em ${format(new Date(createdAt), "dd MMM", { locale: ptBR })}`}>
              <Calendar className="w-4 h-4" aria-hidden="true" />
              <span>{format(new Date(createdAt), "dd MMM", { locale: ptBR })}</span>
            </div>
            <div className="flex items-center gap-1" aria-label={`${readingTime} minutos de leitura`}>
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span>{readingTime} min</span>
            </div>
            <div className="flex items-center gap-1" aria-label={`${views} visualizações`}>
              <Eye className="w-4 h-4" aria-hidden="true" />
              <span>{views}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}